import React, { forwardRef } from 'react';
import classNames from 'classnames';
import moment from 'moment';

import UnitDetails from '../UnitAssignment/UnitDetails';
import SlotArrivalStatus from './SlotArrivalStatus';
import ActionsDropdown from './ActionsDropdown';
import ShiftSlotDisplay from './ShiftSlotDisplay';
import editPencil from '../../../assets/images/edit-pencil.svg';
import Badge from '../../../components/badges/BaseBadge';
import CompassIcon from '../../../components/icons/CompassIcon';
import GuaranteedIcon from '../../../components/icons/GuaranteedIcon';
import BundleBadge from '../BundleBadge';
import ShiftTypeBadge from '../ShiftTypeBadge';
import { GLOBAL_CALENDAR_SHIFT_TYPES } from '../../../constants';
import OnCallSlotBadge from '../OnCallSlotBadge';

const ShiftSlotMobile = forwardRef(
	(
		{
			slot,
			units,
			user,
			updateSlotArrivalStatus,
			onConfirm,
			onPrepareCancel,
			handleEditBonusModalButton,
			handleAddBonusModalButton,
			preventGuaranteeOrBonusUpdates,
			onGuaranteedCheckboxChange,
			onGuaranteeBundleChange,
			setShowUnitAssignmentModal,
			setUnitAssignmentSlot,
			getUnitName,
			onAssign,
			routeSlotAction,
			shouldShowRouteBadges,
			facilityFloatSlotAction,
			onSplitShift
		},
		ref
	) => {
		const MobileRow = ({ label, children, column, className }) => (
			<div className="row">
				<div className="shift-label col-3">{label}</div>
				<div className={classNames('col-9', className, { 'd-flex flex-wrap': !column })}>{children}</div>
			</div>
		);

		return (
			<div
				ref={ref}
				className={classNames('shift-card row with-border with-padding slot', {
					[`slot__${slot.is_flex ? 'flex' : 'shiftmed'}`]: shouldShowRouteBadges && slot.caregiver_name
				})}
			>
				{shouldShowRouteBadges && (
					<MobileRow label="Route" className="with-gap-6">
						<div className="route-badge">
							<ShiftTypeBadge
								shiftType={slot.is_flex ? GLOBAL_CALENDAR_SHIFT_TYPES.FLEX : GLOBAL_CALENDAR_SHIFT_TYPES.ON_DEMAND}
							/>
						</div>
						{!slot.caregiver_id && !slot.is_flex && !!slot.is_hybrid && (
							<div className="route-badge">
								<ShiftTypeBadge shiftType={GLOBAL_CALENDAR_SHIFT_TYPES.FLEX} />
							</div>
						)}
					</MobileRow>
				)}

				<MobileRow label="Assignment">
					<span className="font-weight-bold">{slot.resource_type} &nbsp;&nbsp;</span>{' '}
					<UnitDetails unitId={slot.unit_id} />
				</MobileRow>

				<MobileRow label="Date">
					<span className="week-day">{slot.shift_day},&nbsp;</span>
					<span className="month-date">{slot.shift_date}</span>
				</MobileRow>

				<MobileRow label="Time">
					{moment.utc(slot.raw_start_time).format('hh:mm A')} - {moment.utc(slot.raw_end_time).format('hh:mm A')}
				</MobileRow>

				<MobileRow label="Professional" className="caregiver" column>
					<ShiftSlotDisplay slot={slot} isMobile />
				</MobileRow>

				<MobileRow label="Details" className="mobile-badges-row with-gap-4">
					{slot.is_flex ? (
						'N/A'
					) : (
						<>
							{slot.is_guaranteed && (
								<Badge
									badgeCopy="Guaranteed"
									icon={<GuaranteedIcon width={16} height={16} fillColor="#00b4c3" color="#FFFFFF" />}
									className="guaranteed-badge"
								/>
							)}
							{slot.contract_id && (
								<BundleBadge shiftsCount={slot.contract_non_cancelled_slots_count} bundleId={slot.contract_id} />
							)}
							{slot.is_orientation && (
								<Badge badgeCopy="Orientation" className="orientation-badge" icon={<CompassIcon />} />
							)}
							{slot.has_integration_badge && <Badge badgeCopy="Integrated Shift" className="integrated-badge" />}
							{slot.is_on_call_shift && <OnCallSlotBadge slot={slot} />}
						</>
					)}
				</MobileRow>

				<MobileRow label="Bonus">
					{slot.is_flex ? (
						'N/A'
					) : (
						<>
							<b
								className={classNames('bonus-value', {
									'show-mobile-add': !slot.caregiver_name && !slot.facility_payable_bonus
								})}
							>
								{slot.facility_payable_bonus ? `$${slot.facility_payable_bonus}` : ''}
							</b>
							{slot.facility_payable_bonus ? (
								<img
									className={classNames('edit-pencil-icon', {
										show: !slot.caregiver_name
									})}
									role="presentation"
									src={editPencil}
									alt="Edit"
									onKeyDown={handleEditBonusModalButton}
									onClick={handleEditBonusModalButton}
								/>
							) : (
								<img
									className={classNames('edit-pencil-icon', {
										show: !slot.caregiver_name,
										'add-icon': !slot.facility_payable_bonus && !slot.caregiver_name
									})}
									role="presentation"
									src={editPencil}
									alt="Add"
									onKeyDown={handleAddBonusModalButton}
									onClick={handleAddBonusModalButton}
								/>
							)}
						</>
					)}
				</MobileRow>

				<MobileRow label="Status" column>
					{slot.is_flex ? 'N/A' : <SlotArrivalStatus slot={slot} updateSlotArrivalStatus={updateSlotArrivalStatus} />}
				</MobileRow>

				<MobileRow label="Actions">
					<ActionsDropdown
						isFlex={slot.is_flex}
						canGuarantee={!slot.is_orientation && !preventGuaranteeOrBonusUpdates}
						getUnitName={getUnitName}
						hasAvailableUnits={units.length > 0}
						hasUnitsEnabled={user.hasUnitsEnabled}
						onCancel={onPrepareCancel}
						onAssign={onAssign}
						onConfirm={onConfirm}
						onGuaranteedCheckboxChange={onGuaranteedCheckboxChange}
						onGuaranteeBundleChange={onGuaranteeBundleChange}
						permissions={user.permissions}
						setShowUnitAssignmentModal={setShowUnitAssignmentModal}
						setUnitAssignmentSlot={setUnitAssignmentSlot}
						slot={slot}
						onRouteSlot={routeSlotAction}
						onFacilityFloatSlot={facilityFloatSlotAction}
						onSplitShift={onSplitShift}
					/>
				</MobileRow>
			</div>
		);
	}
);

export default ShiftSlotMobile;
